import { Flex } from '@chakra-ui/react';
import { SIDE_PANEL_COLOR_PICKER_HEIGHT } from 'constants/editor';
import { ColorPicker } from 'modules/Editor/components/ColorPicker';
import { ChartSettingsEntry } from 'modules/Editor/components/SideMenuSettings/ChartSettings';
import { FontFamilySelect } from 'modules/common/components/FontFamilySelect';
import { DropdownFontSizeSelect } from 'modules/common/components/FontSizeSelect';
import { Bold, Italic } from 'modules/common/components/FontStyle';
import React, { ChangeEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChartWidgetData } from 'widgets/ChartWidgets/ChartWidget.types';
import { useSideMenuSetting, useWidget } from 'widgets/sdk';

type AllFontSettingPropsType = {
  fontSize: string;
  fontFamily: string;
  onChangeFontSize: (event: ChangeEvent<HTMLSelectElement>) => void;
  onChangeFontFamily: (fontFamily: string) => void;
};

export type FontSettingsProps =
  | AllFontSettingPropsType
  | (Omit<AllFontSettingPropsType, 'fontSize' | 'onChangeFontSize'> & {
      fontSize?: never;
      onChangeFontSize?: never;
    });

export const FontSettings = ({ fontSize, onChangeFontSize, fontFamily, onChangeFontFamily }: FontSettingsProps) => {
  const { t } = useTranslation('chart_widget', { useSuspense: false, keyPrefix: 'font' });
  const label = t('label');
  const fontSizeLabel = t('fontSize');

  const showFontSize = fontSize !== undefined && !!onChangeFontSize;

  return (
    <Flex alignItems='center' gap={3} justifyContent='space-between' color='font.500' fontSize='sm' py='2'>
      {label}
      <Flex alignItems='right' gap={3}>
        <FontFamilySelect
          contentProps={{ height: SIDE_PANEL_COLOR_PICKER_HEIGHT }}
          fontFamily={fontFamily}
          onChange={onChangeFontFamily}
          borderRadius='base'
          width='36'
          buttonProps={{ size: 'xs' }}
          listenScrollEvent={true}
        />
        {showFontSize && (
          <DropdownFontSizeSelect
            value={parseInt(fontSize)}
            onChange={onChangeFontSize}
            width='16'
            size='xs'
            aria-label={fontSizeLabel}
            borderRadius='base'
          />
        )}
      </Flex>
    </Flex>
  );
};

type AllTextSettingPropsType = {
  fontColor: string;
  fontStyle: string | number | true;
  fontWeight: string;
  fontFamily: string;
  onFontColorChange: (fontColor: string) => void;
  onFontStyleChange: (fontStyle: string) => void;
  onFontWeightChange: (fontWeight: string) => void;
};

export type TextSettingsProps =
  | AllTextSettingPropsType
  | (Omit<AllTextSettingPropsType, 'fontColor' | 'onFontColorChange'> & {
      fontColor?: never;
      onFontColorChange?: never;
    });

export const TextSettings = React.memo(
  ({
    fontColor,
    fontStyle,
    fontWeight,
    fontFamily,
    onFontColorChange,
    onFontStyleChange,
    onFontWeightChange,
  }: TextSettingsProps) => {
    const { t } = useTranslation('chart_widget', { useSuspense: false });
    const label = t('text.label');
    const colorLabel = t('text.colorLabel');

    const { setAllowScrolling } = useSideMenuSetting();

    const onFontWeightClick = () => {
      const newFontWeight = fontWeight === 'normal' ? 'bold' : 'normal';
      onFontWeightChange(newFontWeight);
    };

    const onFontStyleClick = () => {
      const newFontStyle = fontStyle === 'normal' ? 'italic' : 'normal';
      onFontStyleChange(newFontStyle);
    };

    const showColorPicker = fontColor !== undefined && !!onFontColorChange;

    return (
      <ChartSettingsEntry label={label}>
        {showColorPicker && (
          <ColorPicker
            iconStyle='text'
            color={fontColor}
            label={colorLabel}
            placement={'left-start'}
            onChange={onFontColorChange}
            onOpen={setAllowScrolling.off}
            onClose={setAllowScrolling.on}
          />
        )}
        <Bold fontFamily={fontFamily} isActive={fontWeight === 'bold'} onChange={onFontWeightClick} />
        <Italic fontFamily={fontFamily} isActive={fontStyle === 'italic'} onChange={onFontStyleClick} />
      </ChartSettingsEntry>
    );
  },
);
TextSettings.displayName = 'TextSettings';

export const LegendFontColor = () => {
  const {
    updateWidget,
    legend: {
      labelStyle: { color },
    },
  } = useWidget<ChartWidgetData>();

  const { t } = useTranslation('chart_widget', { useSuspense: false, keyPrefix: 'text' });
  const label = t('colorLabel');

  const onChangeLegendColor = useCallback(
    (color: string) =>
      updateWidget({
        legend: { labelStyle: { color } },
      }),
    [updateWidget],
  );

  const { setAllowScrolling } = useSideMenuSetting();

  return (
    <ColorPicker
      iconStyle='text'
      color={color}
      label={label}
      placement={'left-start'}
      onChange={onChangeLegendColor}
      onOpen={setAllowScrolling.off}
      onClose={setAllowScrolling.on}
    />
  );
};

export const LegendFontWeight = () => {
  const {
    updateWidget,
    legend: {
      labelStyle: { fontWeight, fontFamily },
    },
  } = useWidget<ChartWidgetData>();

  const onClickFontWeight = useCallback(() => {
    const newValue = fontWeight === 'normal' ? 'bold' : 'normal';

    updateWidget({
      legend: { labelStyle: { fontWeight: newValue } },
    });
  }, [fontWeight, updateWidget]);

  return <Bold fontFamily={fontFamily} isActive={fontWeight === 'bold'} onChange={onClickFontWeight} />;
};

export const LegendFontStyle = () => {
  const {
    updateWidget,
    legend: {
      labelStyle: { fontStyle, fontFamily },
    },
  } = useWidget<ChartWidgetData>();

  const onClickFontStyle = useCallback(() => {
    const newValue = fontStyle === 'normal' ? 'italic' : 'normal';

    updateWidget({
      legend: { labelStyle: { fontStyle: newValue } },
    });
  }, [fontStyle, updateWidget]);

  return <Italic fontFamily={fontFamily} isActive={fontStyle === 'italic'} onChange={onClickFontStyle} />;
};

export const LegendFontSettings = () => {
  const {
    updateWidget,
    legend: {
      labelStyle: { fontSize, fontFamily },
    },
  } = useWidget<ChartWidgetData>();

  const onChangeFontSize = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) =>
      updateWidget({
        legend: { labelStyle: { fontSize: event.target.value + 'px' } },
      }),
    [updateWidget],
  );

  const onChangeFontFamily = useCallback(
    (fontFamily: string) =>
      updateWidget({
        legend: { labelStyle: { fontFamily } },
      }),
    [updateWidget],
  );

  return (
    <FontSettings
      fontFamily={fontFamily}
      fontSize={fontSize}
      onChangeFontFamily={onChangeFontFamily}
      onChangeFontSize={onChangeFontSize}
    />
  );
};
